/**
 * Session state.
 *
 * On mount the app asks the API who the caller is. That call also recovers the
 * CSRF token, which lives in memory only and is therefore lost on reload while
 * the HttpOnly session cookie survives.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ApiError, api, setCsrfToken, type CurrentUser } from "./api/client";

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  logIn: (email: string, password: string) => Promise<void>;
  logOut: () => Promise<void>;
  /** After a profile change: show the new name without a reload. */
  setUser: (user: CurrentUser) => void;
  /** After something ended this session (a password change): back to the sign-in page. */
  signedOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api
      .currentUser()
      .then((current) => {
        if (!cancelled) setUser(current);
      })
      .catch((error: unknown) => {
        // A 401 here is the normal "not logged in" case, not a failure.
        if (!(error instanceof ApiError) || !error.isUnauthenticated) {
          console.error("failed to resolve session", error);
        }
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const logIn = useCallback(async (email: string, password: string) => {
    const session = await api.logIn(email, password);
    setUser({
      user_id: session.user_id,
      email: session.email,
      display_name: session.display_name,
      csrf_token: session.csrf_token,
      is_admin: session.is_admin ?? false,
    });
  }, []);

  const logOut = useCallback(async () => {
    try {
      await api.logOut();
    } finally {
      // Clear local state even if the request failed: the user asked to leave.
      setCsrfToken(null);
      setUser(null);
    }
  }, []);

  const signedOut = useCallback(() => {
    setCsrfToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, logIn, logOut, setUser, signedOut }),
    [user, loading, logIn, logOut, signedOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside an AuthProvider");
  }
  return context;
}
